import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  templateUrl: './admin-users.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminUsersComponent implements OnInit {
  sessionId = '';
  showAdmins = false; // false = Users (admin=0), true = Admins (admin=1)
  page = 0;
  size = 20;
  totalElements = 0;
  totalPages = 0;
  content: any[] = [];
  loading = false;
  error = '';

  sortBy = 'username';
  sortDir: 'asc' | 'desc' = 'asc';

  // Detail modal (detailUser.id null = create mode)
  detailUser: any = null;
  detailTab: 'details' | 'subscriptions' = 'details';
  detailForm: any = {};
  detailSaving = false;
  detailMessage = '';

  // Subscriptions tab
  subscriptionsList: any[] = [];
  availableSubscriptions: any[] = [];
  assignSubscriptionId: number | null = null;
  assignValidFrom = '';
  assignValidTo = '';
  assignLoading = false;

  // Delete confirm
  deleteUser: any = null;
  deleteInProgress = false;

  countries: any[] = [];
  authorities: any[] = [];
  filteredAuthorities: any[] = [];

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    this.setDefaultAssignDates();
    this.loadPage();
  }

  private setDefaultAssignDates() {
    const today = new Date();
    this.assignValidFrom = today.toISOString().slice(0, 10);
    const to = new Date(today);
    to.setFullYear(to.getFullYear() + 1);
    this.assignValidTo = to.toISOString().slice(0, 10);
  }

  get adminOnly(): boolean | undefined {
    return this.showAdmins ? true : false;
  }

  async loadPage() {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      const sort = `${this.sortBy},${this.sortDir}`;
      const res = await this.adminRest.getAdminUsers(this.sessionId, this.page, this.size, this.adminOnly, sort);
      this.content = res.content || [];
      this.totalElements = res.totalElements ?? 0;
      this.totalPages = res.totalPages ?? 0;
    } catch (e: any) {
      this.error = e?.message || 'Failed to load users';
    } finally {
      this.loading = false;
    }
  }

  setSort(column: string) {
    if (this.sortBy === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDir = 'asc';
    }
    this.page = 0;
    this.loadPage();
  }

  toggleFilter() {
    this.showAdmins = !this.showAdmins;
    this.page = 0;
    this.loadPage();
  }

  goToPage(p: number) {
    if (p < 0 || p >= this.totalPages) return;
    this.page = p;
    this.loadPage();
  }

  async openCreate() {
    this.detailUser = { id: null, username: '' };
    this.detailTab = 'details';
    this.detailMessage = '';
    this.detailForm = {
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      active: true,
      admin: false,
      acceptMarketing: false,
      countryId: null as number | null,
      authorityId: null as number | null
    };
    await this.loadCountriesAndAuthoritiesForDetail();
    this.availableSubscriptions = await this.adminRest.getAdminSubscriptions(this.sessionId) || [];
    this.subscriptionsList = [];
  }

  async openDetails(user: any) {
    this.detailUser = null;
    this.detailTab = 'details';
    this.detailMessage = '';
    try {
      this.detailUser = await this.adminRest.getAdminUser(this.sessionId, user.id);
      this.detailForm = {
        username: this.detailUser.username ?? '',
        password: '',
        firstName: this.detailUser.firstName ?? '',
        lastName: this.detailUser.lastName ?? '',
        email: this.detailUser.email ?? '',
        active: this.detailUser.active ?? false,
        admin: this.detailUser.admin ?? false,
        acceptMarketing: this.detailUser.acceptMarketing ?? false,
        countryId: this.detailUser.countryId ?? null,
        authorityId: this.detailUser.authorityId ?? null
      };
      await this.loadCountriesAndAuthoritiesForDetail();
      this.availableSubscriptions = await this.adminRest.getAdminSubscriptions(this.sessionId) || [];
      this.subscriptionsList = [];
      await this.loadUserSubscriptions();
    } catch (e: any) {
      this.detailMessage = e?.message || 'Failed to load user';
    }
  }

  private async loadCountriesAndAuthoritiesForDetail() {
    this.countries = await this.restService.getCountries(this.sessionId) || [];
    // Load all authorities (not restricted by country); display as "Country - Authority"
    this.authorities = await this.restService.getAuthorities(this.sessionId) || [];
    this.filteredAuthorities = this.authorities;
  }

  private async loadUserSubscriptions() {
    if (!this.detailUser?.id || !this.sessionId) return;
    const res = await this.adminRest.getAdminUserSubscriptions(this.sessionId, this.detailUser.id);
    this.subscriptionsList = res?.content || [];
  }

  onCountryChange() {
    // When no authority selected yet, preselect the first authority of the chosen country
    if (this.detailForm.authorityId == null && this.detailForm.countryId != null && this.authorities.length > 0) {
      const firstOfCountry = this.authorities.find((a: any) => a.countryId === this.detailForm.countryId);
      if (firstOfCountry) this.detailForm.authorityId = firstOfCountry.id;
    }
  }

  closeDetails() {
    this.detailUser = null;
    this.page = 0;
    this.loadPage();
  }

  async saveDetails() {
    if (!this.sessionId) return;
    this.detailSaving = true;
    this.detailMessage = '';
    try {
      const isCreate = this.detailUser?.id == null;
      if (isCreate) {
        const username = (this.detailForm.username ?? '').trim();
        const password = this.detailForm.password ?? '';
        if (!username || !password) {
          this.detailMessage = 'Username and password are required.';
          this.detailSaving = false;
          return;
        }
        const created = await this.adminRest.postAdminUser(this.sessionId, {
          username,
          password,
          firstName: this.detailForm.firstName ?? '',
          lastName: this.detailForm.lastName ?? '',
          email: this.detailForm.email ?? '',
          active: this.detailForm.active ?? true,
          admin: this.detailForm.admin ?? false,
          acceptMarketing: this.detailForm.acceptMarketing ?? false,
          countryId: this.detailForm.countryId ?? null,
          authorityId: this.detailForm.authorityId ?? null
        });
        if (created?.id) {
          this.detailMessage = 'User created.';
          this.detailUser = await this.adminRest.getAdminUser(this.sessionId, created.id);
          this.detailForm.password = '';
        } else {
          this.detailMessage = 'Create failed (username may already exist).';
        }
      } else {
        const ok = await this.adminRest.putAdminUser(this.sessionId, this.detailUser.id, {
          firstName: this.detailForm.firstName,
          lastName: this.detailForm.lastName,
          email: this.detailForm.email,
          active: this.detailForm.active,
          admin: this.detailForm.admin,
          acceptMarketing: this.detailForm.acceptMarketing,
          countryId: this.detailForm.countryId,
          authorityId: this.detailForm.authorityId
        });
        if (ok) {
          this.detailMessage = 'Saved.';
          this.detailUser = await this.adminRest.getAdminUser(this.sessionId, this.detailUser.id);
        } else {
          this.detailMessage = 'Save failed.';
        }
      }
    } catch (e: any) {
      this.detailMessage = e?.message || 'Save failed.';
    } finally {
      this.detailSaving = false;
    }
  }

  save() {
    this.saveDetails();
  }

  async assignSubscription() {
    if (!this.detailUser?.id || !this.assignSubscriptionId || !this.sessionId) return;
    this.assignLoading = true;
    this.detailMessage = '';
    try {
      const ok = await this.adminRest.postAdminUserSubscription(this.sessionId, {
        userId: this.detailUser.id,
        subscriptionId: this.assignSubscriptionId,
        validFrom: this.assignValidFrom,
        validTo: this.assignValidTo
      });
      if (ok) {
        this.detailMessage = 'Subscription assigned.';
        this.assignSubscriptionId = null;
        this.setDefaultAssignDates();
        await this.loadUserSubscriptions();
      } else {
        this.detailMessage = 'Assign failed.';
      }
    } catch (e: any) {
      this.detailMessage = e?.message || 'Assign failed.';
    } finally {
      this.assignLoading = false;
    }
  }

  async cancelSubscription(us: any) {
    if (!confirm('Cancel this subscription assignment? This cannot be undone.')) return;
    if (!this.sessionId) return;
    const ok = await this.adminRest.deleteAdminUserSubscription(this.sessionId, us.id);
    if (ok) {
      this.detailMessage = 'Subscription cancelled.';
      await this.loadUserSubscriptions();
    } else {
      this.detailMessage = 'Cancel failed.';
    }
  }

  openDeleteConfirm(user: any) {
    this.deleteUser = user;
  }

  closeDeleteConfirm() {
    this.deleteUser = null;
  }

  async confirmDelete() {
    if (!this.deleteUser?.id || !this.sessionId) return;
    this.deleteInProgress = true;
    try {
      const ok = await this.adminRest.deleteAdminUser(this.sessionId, this.deleteUser.id);
      if (ok) {
        this.closeDeleteConfirm();
        if (this.detailUser?.id === this.deleteUser.id) this.closeDetails();
        await this.loadPage();
      }
    } finally {
      this.deleteInProgress = false;
    }
  }
}
